/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { ServiceRequest } from "@src/app/model/serviceRequest";
import { ChartParms, TableView } from "@src/app/model/sys/nav-item";
import { User } from "@src/app/model/user";
import { NavRoute } from "@src/app/shared/NavRoute";
import { ServiceRequestPageComponent } from "@src/app/modules/crm/service-request-page/service-request-page.component";

/* No setNewComponent: a request is raised in the stepper dialog, so there is no crm/requests/NEW. */
export const ServiceRequestPageNavRoute = new NavRoute('crm/requests', ()=>ServiceRequestPageComponent, 'live_help')
    .setCharts([
    { type: 'pie', picklist: 'requestType' } as ChartParms,
    { type: 'pie', picklist: 'srStatus' } as ChartParms,
    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
    { type: 'pie', picklist: 'subStatus' } as ChartParms,
])
.setViews((u: User) => {
    if (!u.currentTeam) {
        return null;
    }

    if (!u.inAgency) {
        const views:TableView[]  = [{
                id: 'omc_default_open',
                name: $localize`All Open Requests`,
                helpText: 'All open service requests',
                filterFields: { refNr: null, srStatus: ServiceRequest.srOpen.id, subStatus: 'All',
                    priorityId: 'All',keyword: null, requestType: null },
                sort: 'id',
                charts: [
                    { type: 'pie', picklist: 'requestType' } as ChartParms,
                    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                    { type: 'bar', picklist: 'srClass' } as ChartParms,                    
                    { type: 'pie', picklist: 'subStatus' } as ChartParms,
                    { type: 'pie', picklist: 'priorityId' } as ChartParms,
                ]
        },{
                id: 'omc_directors_open',
                name: $localize`Directors Open Requests`,
                helpText: 'Requests that are escalated to Directors',
                filterFields: { refNr: null, srStatus: ServiceRequest.srOpen.id, subStatus: 'All',
                    priorityId: 'All',keyword: null, forumId: 'NOT NULL', requestType: null },
                sort: 'id',
                charts: [
                    { type: 'pie', picklist: 'requestType' } as ChartParms,
                    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                    { type: 'bar', picklist: 'srClass' } as ChartParms,                    
                    { type: 'pie', picklist: 'subStatus' } as ChartParms,
                    { type: 'pie', picklist: 'priorityId' } as ChartParms,
                ]
        },{           
                id: 'omc_for_directors_approval',
                name: $localize`Directors To Be Approved List`,
                helpText: 'Requests awaiting director approval',
                filterFields: { 
                    refNr: null,
                    srStatus: ServiceRequest.srOpen.id,
                    forumId: 'NOT NULL',
                    subStatus: 'All', priorityId: 'All',keyword: null, approvedById: 'NULL',
                    requestType: null
                },
                sort: 'id',
                charts: [
                    { type: 'pie', picklist: 'requestType' } as ChartParms,
                    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                    { type: 'bar', picklist: 'srClass' } as ChartParms,
                    { type: 'pie', picklist: 'subStatus' } as ChartParms,
                    { type: 'pie', picklist: 'priorityId' } as ChartParms,
                ]
        },{
                id: 'omc__director_approved',
                name: $localize`Requests Approved for Action by Directors`,
                helpText: 'Channel requests approved by a director',
            filterFields: { refNr: null,
                srStatus: ServiceRequest.srOpen.id,
                forumId: 'NOT NULL',
                subStatus: 'All', priorityId: 'All',keyword: null, approvedById: 'NOT NULL',
                requestType: null },
                sort: 'id',
                charts: [
                    { type: 'pie', picklist: 'requestType' } as ChartParms,
                    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                    { type: 'bar', picklist: 'srClass' } as ChartParms,
                    { type: 'pie', picklist: 'subStatus' } as ChartParms,
                    { type: 'pie', picklist: 'priorityId' } as ChartParms,
                ]
        }];
/*
        u.forumRoles.forEach( (fr) =>  {
            views.push({
                id: fr.id as string,
                name: $localize `Open ${fr.forumName} Requests`,
                helpText: $localize `Open requests assigned to the ${fr.forumName} channel`,
                filterFields: {
                    refNr: null,
                    srStatus: ServiceRequest.srOpen.id,
                    forumId: fr.forumId as string,
                    subStatus: 'All', priorityId: 'All',
                    keyword: null
                },
                sort: 'id',
                charts: [
                    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                    { type: 'pie', picklist: 'subStatus' } as ChartParms,
                    { type: 'pie', picklist: 'priorityId' } as ChartParms,
                ]
            });
        });
*/
        views.push({
            id: 'newest_open',
            name: $localize`Newest Open Requests`,
            helpText: 'Open requests sorted newest first',
            filterFields: { srStatus: ServiceRequest.srOpen.id, priorityId: 'All', subStatus: 'All',
                keyword: null, ownedById: 'All', requestType: null },
            sortDesc: 'createdAt',
            charts: [
                { type: 'pie', picklist: 'requestType' } as ChartParms,
                { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                { type: 'bar', picklist: 'srClass' } as ChartParms,
                { type: 'pie', picklist: 'subStatus' } as ChartParms,
                { type: 'pie', picklist: 'priorityId' } as ChartParms,
            ]
        });
        views.push({
            id: 'open_agenda_items',
            name: $localize`Open Agenda Items`,
            helpText: 'Open requests linked to a ballot agenda item',
            filterFields: { srStatus: ServiceRequest.srOpen.id, subStatus: 'All', keyword: null,
                ballotId: 'NOT NULL', requestType: null },
            sort: 'id',
            charts: [
                { type: 'pie', picklist: 'requestType' } as ChartParms,
                { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                { type: 'pie', picklist: 'subStatus' } as ChartParms,
            ]
        });
        views.push({
            id: 'omc_bill_todo',
            name: $localize`Items To Be Billed`,
            helpText: 'Resolved work orders waiting to be billed',
            filterFields: { srStatus: ServiceRequest.srResolved.id,
                requestType: ServiceRequest.typeWorkOrder.id },
            sort: 'refNr',
            charts: [
                { type: 'trend', picklist: 'srStatus', trend: 'weekCreated' } as ChartParms,
                { type: 'pie', picklist: 'srClass' } as ChartParms,
            ]
        });
        return views;
    }

    return [
        {
            id: 'my_default',
            name:  $localize `My Open Requests`,
            helpText: 'Your open requests across all OMCs; change the filter to view another team member\'s requests',
            filterFields: {refNr: null, ownedById: u.id, srStatus: ServiceRequest.srOpen.id,
                subStatus: 'All', teamId: 'All', keyword: null, requestType: null},
            sort: 'id',
            charts: [
                { type: 'pie', picklist: 'requestType' } as ChartParms,
                { type: 'pie', picklist: 'subStatus' } as ChartParms,
                /* { type: 'pie', picklist: 'omc_managerId' } as ChartParms, */
                { type: 'bar', picklist: 'teamId' } as ChartParms,
                { type: 'bar', picklist: 'srClass' } as ChartParms,
                { type: 'pie', picklist: 'priorityId' } as ChartParms,
            ]
        },
        {
            id: 'my_omc_default',
            name: $localize `My OMCs Open Requests`,
            helpText: 'Open requests across OMCs you manage; change the filter to view another manager\'s OMCs',
            filterFields: { omc_managerId: u.id, srStatus: ServiceRequest.srOpen.id,
                subStatus: 'All', teamId: 'All', keyword: null, requestType: null },
            sort: 'id',
            charts: [
                { type: 'pie', picklist: 'requestType' } as ChartParms,
                { type: 'pie', picklist: 'subStatus' } as ChartParms,
                { type: 'bar', picklist: 'teamId' } as ChartParms,
                { type: 'bar', picklist: 'srClass' } as ChartParms,
                { type: 'pie', picklist: 'priorityId' } as ChartParms,
            ]
        },
        {
            id: 'all_default',
            name: $localize `All Open Requests`,
            helpText: 'All open requests across all OMCs',
            filterFields: { srStatus: ServiceRequest.srOpen.id, subStatus: 'All',  teamId: 'All',
                keyword: null, requestType: null},
            sort: 'id',
            charts: [
                { type: 'pie', picklist: 'requestType' } as ChartParms,
                { type: 'pie', picklist: 'omc_managerId' } as ChartParms,
                { type: 'bar', picklist: 'teamId' } as ChartParms,
                { type: 'pie', picklist: 'subStatus' } as ChartParms,
                { type: 'pie', picklist: 'priorityId' } as ChartParms,
            ]
        },
        {
            id: 'all_newest_open',
            name: $localize `Newest Open Requests`,
            helpText: 'All open requests sorted newest first',
            filterFields: { srStatus: ServiceRequest.srOpen.id, subStatus: 'All', teamId: 'All',
                keyword: null, ownedById: 'All', requestType: null },
            sortDesc: 'createdAt',
            charts: [
                { type: 'pie', picklist: 'requestType' } as ChartParms,
                { type: 'pie', picklist: 'omc_managerId' } as ChartParms,
                { type: 'bar', picklist: 'teamId' } as ChartParms,
                { type: 'pie', picklist: 'subStatus' } as ChartParms,
                { type: 'pie', picklist: 'priorityId' } as ChartParms,
            ]
        },
        {
            id: 'all_open_channel',
            name: $localize`All Open Channel Requests`,
            helpText: 'All open channel requests across all OMCs',
            filterFields: { refNr: null, teamId: 'All',
                srStatus: ServiceRequest.srOpen.id,
                forumId: 'NOT NULL',
                subStatus: 'All', priorityId: 'All',keyword: null, requestType: null },
                sort: 'id',
                charts: [
                    { type: 'pie', picklist: 'requestType' } as ChartParms,
                    { type: 'bar', picklist: 'teamId' } as ChartParms,
                    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                    { type: 'pie', picklist: 'subStatus' } as ChartParms,
                    { type: 'pie', picklist: 'priorityId' } as ChartParms,
                ]
        },
        {
            id: 'all_unapproved_channel',
            name: $localize`Unapproved Channel Requests`,
            helpText: 'All open channel requests across all OMCs',
            filterFields: { refNr: null, teamId: 'All',
                srStatus: ServiceRequest.srOpen.id,
                forumId: 'NOT NULL',
                subStatus: 'All', priorityId: 'All',keyword: null, approvedById: 'NULL',
                requestType: null },
                sort: 'id',
                charts: [
                    { type: 'pie', picklist: 'requestType' } as ChartParms,
                    { type: 'bar', picklist: 'teamId' } as ChartParms,
                    { type: 'bar', picklist: 'monthOpened' } as ChartParms,
                    { type: 'pie', picklist: 'subStatus' } as ChartParms,
                    { type: 'pie', picklist: 'priorityId' } as ChartParms,
                ]
        },        
        {
            id: 'search_by_ref',
            name: $localize`Search By Reference`,
            helpText: 'Search for a request by reference number',
            filterFields: { refNr: '----', teamId: 'All', keyword: null, requestType: null },
            sort: 'id',
        },
        {
            id: 'all_analysis',
            name: $localize `All Requests`,
            helpText: 'All requests including closed, for trend analysis',
            filterFields: {srStatus: 'All', subStatus: 'All', teamId: 'All', keyword: '', requestType: null},
            sort: 'id',
            charts: [
                { type: 'pie', picklist: 'requestType' } as ChartParms,
                { type: 'pie', picklist: 'omc_managerId' } as ChartParms,
                { type: 'trend', picklist: 'srStatus', trend: 'weekCreated' } as ChartParms,
                //{ type: 'bar', picklist: 'templateId' } as ChartParms,
                { type: 'pie', picklist: 'priorityId' } as ChartParms,
            ]
        },
        {
            id: 'bill_todo',
            name: $localize`Items To Be Billed`,
            helpText: 'Resolved work orders waiting to be billed',
            filterFields: { srStatus: ServiceRequest.srResolved.id,
                requestType: ServiceRequest.typeWorkOrder.id, teamId: null, omc_managerId: null },
            sort: 'teamId,refNr',
            charts: [
                { type: 'pie', picklist: 'omc_managerId' } as ChartParms,
                { type: 'trend', picklist: 'srStatus', trend: 'weekCreated' } as ChartParms,
                { type: 'bar', picklist: 'teamId' } as ChartParms,
                { type: 'pie', picklist: 'srClass' } as ChartParms,
            ]
        },
    ]
})
